import { useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogBody, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Plus, Trash2 } from "lucide-react"
import { useState } from "react"
import { router } from "@inertiajs/react"
import type { ProjectNote } from "@/types"

type Props = {
  projectId: string
  notes: ProjectNote[]
}

export function NotesTab({ projectId, notes }: Props) {
  const [showForm, setShowForm] = useState(false)

  const { data, setData, post, processing, reset } = useForm({
    title: "",
    description: "",
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/projects/${projectId}/project_notes`, {
      onSuccess: () => {
        reset()
        setShowForm(false)
      },
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Notes</h3>
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          <Plus className="size-4" />
          Ajouter
        </Button>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle note</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
            <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="note-title">Titre</Label>
              <Input
                id="note-title"
                value={data.title}
                onChange={(e) => setData("title", e.target.value)}
                placeholder="Titre (optionnel)"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="note-description">Description</Label>
              <Textarea
                id="note-description"
                value={data.description}
                onChange={(e) => setData("description", e.target.value)}
                required
                rows={3}
              />
            </div>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={processing}>
                Enregistrer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {notes.length === 0 ? (
        <p className="text-muted-foreground text-sm">Aucune note</p>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <Card key={note.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">
                    {note.title || "Note"}
                    <span className="text-muted-foreground ml-2 font-normal text-xs">
                      par {note.author?.first_name} {note.author?.last_name} —{" "}
                      {new Date(note.created_at).toLocaleDateString("fr-FR")}
                    </span>
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    onClick={() => router.delete(`/projects/${projectId}/project_notes/${note.id}`)}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{note.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
